import { EnemyType } from '@/levels/EnemyTypes';

export interface LevelObjective {
  type: 'killEnemies' | 'survive'; // survive: target is in seconds
  target: number;
  current?: number;
}

export type EnemyWeights = Partial<Record<EnemyType, number>>;

export interface LevelConfig {
  id: number;
  name: string;
  description: string;
  deploymentLimit: number; // most roster ships that can launch on this mission
  reinforceEvery: number; // kills per replacement ship, 0 = none
  objective: LevelObjective;
  enemySpawnRate: number;
  maxEnemies: number;
  enemyWeights: EnemyWeights;
  unlocked?: boolean;
  completed?: boolean;
}

const CLASSIC_MIX: EnemyWeights = { standard: 70, railgun: 15, torpedo: 15 };

export const LEVELS: LevelConfig[] = [
  {
    id: 1,
    name: "First Contact",
    description: "Defend against the first wave of enemies",
    deploymentLimit: 1,
    reinforceEvery: 0,
    objective: { type: 'killEnemies', target: 5, current: 0 },
    enemySpawnRate: 3000,
    maxEnemies: 3,
    enemyWeights: CLASSIC_MIX,
    unlocked: true
  },
  {
    id: 2,
    name: "Reinforcements",
    description: "Command two ships against stronger forces",
    deploymentLimit: 2,
    reinforceEvery: 5,
    objective: { type: 'killEnemies', target: 10, current: 0 },
    enemySpawnRate: 2500,
    maxEnemies: 5,
    enemyWeights: CLASSIC_MIX,
    unlocked: false
  },
  {
    id: 3,
    name: "Fleet Command",
    description: "Lead a small fleet of three ships",
    deploymentLimit: 3,
    reinforceEvery: 6,
    objective: { type: 'killEnemies', target: 15, current: 0 },
    enemySpawnRate: 2000,
    maxEnemies: 7,
    enemyWeights: CLASSIC_MIX,
    unlocked: false
  },
  {
    id: 4,
    name: "Final Stand",
    description: "Command your full fleet in the final battle",
    deploymentLimit: 4,
    reinforceEvery: 6,
    objective: { type: 'killEnemies', target: 20, current: 0 },
    enemySpawnRate: 1500,
    maxEnemies: 10,
    enemyWeights: CLASSIC_MIX,
    unlocked: false
  },
  {
    id: 5,
    name: "Endless Onslaught",
    description: "Elite squadron versus overwhelming enemy forces",
    deploymentLimit: 5,
    reinforceEvery: 8,
    objective: { type: 'killEnemies', target: 50, current: 0 },
    enemySpawnRate: 1200, // Very fast spawning
    maxEnemies: 15, // Many enemies on screen
    enemyWeights: CLASSIC_MIX,
    unlocked: false
  },
  {
    id: 6,
    name: "The Swarm",
    description: "Kamikaze swarmers dive in packs. Keep moving and let point defense work.",
    deploymentLimit: 3,
    reinforceEvery: 8,
    objective: { type: 'killEnemies', target: 30, current: 0 },
    enemySpawnRate: 1800,
    maxEnemies: 12,
    enemyWeights: { swarmer: 60, standard: 40 },
    unlocked: false
  },
  {
    id: 7,
    name: "Iron Wall",
    description: "Dreadnoughts advance behind a screen of raiders. Focus fire on the big ones.",
    deploymentLimit: 3,
    reinforceEvery: 5,
    objective: { type: 'killEnemies', target: 15, current: 0 },
    enemySpawnRate: 2600,
    maxEnemies: 7,
    enemyWeights: { standard: 55, dreadnought: 30, railgun: 15 },
    unlocked: false
  },
  {
    id: 8,
    name: "Shield Bearers",
    description: "Aegis escorts circle with regenerating shields. Break them before they recover.",
    deploymentLimit: 4,
    reinforceEvery: 6,
    objective: { type: 'killEnemies', target: 20, current: 0 },
    enemySpawnRate: 2200,
    maxEnemies: 8,
    enemyWeights: { aegis: 40, standard: 40, torpedo: 20 },
    unlocked: false
  },
  {
    id: 9,
    name: "Minefield",
    description: "Minelayers are sowing the sector. Survive for 90 seconds.",
    deploymentLimit: 4,
    reinforceEvery: 6,
    objective: { type: 'survive', target: 90, current: 0 },
    enemySpawnRate: 2000,
    maxEnemies: 9,
    enemyWeights: { minelayer: 35, standard: 45, swarmer: 20 },
    unlocked: false
  },
  {
    id: 10,
    name: "Mothership",
    description: "Carriers launch endless fighters. Cut off the source.",
    deploymentLimit: 4,
    reinforceEvery: 6,
    objective: { type: 'killEnemies', target: 30, current: 0 },
    enemySpawnRate: 2200,
    maxEnemies: 8,
    enemyWeights: { carrier: 15, standard: 45, aegis: 20, swarmer: 20 },
    unlocked: false
  },
  {
    id: 11,
    name: "Crossfire",
    description: "Lancers and bombers hold the flanks. Watch for aim lines.",
    deploymentLimit: 5,
    reinforceEvery: 8,
    objective: { type: 'killEnemies', target: 35, current: 0 },
    enemySpawnRate: 1600,
    maxEnemies: 12,
    enemyWeights: { railgun: 35, torpedo: 30, standard: 35 },
    unlocked: false
  },
  {
    id: 12,
    name: "Heavy Metal",
    description: "A battle group of dreadnoughts with shielded escorts.",
    deploymentLimit: 5,
    reinforceEvery: 7,
    objective: { type: 'killEnemies', target: 30, current: 0 },
    enemySpawnRate: 2000,
    maxEnemies: 10,
    enemyWeights: { dreadnought: 35, aegis: 30, standard: 35 },
    unlocked: false
  },
  {
    id: 13,
    name: "Hold the Line",
    description: "Every enemy class at once. Survive for two minutes.",
    deploymentLimit: 5,
    reinforceEvery: 8,
    objective: { type: 'survive', target: 120, current: 0 },
    enemySpawnRate: 1300,
    maxEnemies: 14,
    enemyWeights: {
      standard: 30, railgun: 10, torpedo: 10, swarmer: 15,
      dreadnought: 8, aegis: 10, minelayer: 10, carrier: 7
    },
    unlocked: false
  },
  {
    id: 14,
    name: "Armada",
    description: "The enemy's main fleet. Capital ships everywhere.",
    deploymentLimit: 5,
    reinforceEvery: 10,
    objective: { type: 'killEnemies', target: 60, current: 0 },
    enemySpawnRate: 1500,
    maxEnemies: 14,
    enemyWeights: { carrier: 15, dreadnought: 20, aegis: 20, minelayer: 15, standard: 30 },
    unlocked: false
  },
  {
    id: 15,
    name: "Void Eternal",
    description: "The final battle. Destroy 100 ships of every class.",
    deploymentLimit: 5,
    reinforceEvery: 12,
    objective: { type: 'killEnemies', target: 100, current: 0 },
    enemySpawnRate: 1000,
    maxEnemies: 18,
    enemyWeights: {
      standard: 25, railgun: 12, torpedo: 12, swarmer: 15,
      dreadnought: 10, aegis: 10, minelayer: 8, carrier: 8
    },
    unlocked: false
  }
];

